/**
 * React Query mutations for document section CRUD operations
 * Story 227: Edit PRDs, tech designs and tasks
 * Story 356: Web event instrumentation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { storyDocumentKeys, type DocumentSection, type DocumentResponse } from './useStoryDocument';
import { historyKeys } from './useSectionHistory';
import type { SectionType } from '../types/document';
import { track } from '../lib/analytics';

/** Section update payload */
interface SectionUpdate {
  title?: string;
  body?: string;
  anchor?: string | null;
  covers?: string[];
  section_type?: SectionType; // Story 250
}

/** Section create payload */
interface SectionCreate {
  title: string;
  body: string;
  anchor?: string | null;
  section_key?: string;
}

/** API mutation result */
interface MutationResult {
  success: boolean;
  message?: string;
  id?: string;
}

/**
 * Hook for document section mutations
 *
 * Provides mutations for:
 * - updateSection: Update title, body, or anchor
 * - createSection: Add a new section
 * - deleteSection: Remove a section
 * - reorderSections: Change section order
 */
export function useDocumentMutations(
  projectSlug: string,
  storyId: string,
  docType: string
) {
  const queryClient = useQueryClient();
  const queryKey = storyDocumentKeys.document(projectSlug, storyId, docType);

  const updateSection = useMutation({
    mutationFn: async ({
      sectionKey,
      updates,
    }: {
      sectionKey: string;
      updates: SectionUpdate;
    }): Promise<MutationResult> => {
      const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/documents/${docType}/sections/${encodeURIComponent(sectionKey)}`;
      return api.fetch(endpoint, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
    },
    onMutate: async ({ sectionKey, updates }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<DocumentResponse>(queryKey);

      if (previousData) {
        queryClient.setQueryData<DocumentResponse>(queryKey, {
          ...previousData,
          sections: previousData.sections.map((section) =>
            section.section_key === sectionKey
              ? { ...section, ...updates }
              : section
          ),
        });
      }

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSuccess: (_, { sectionKey, updates }) => {
      // Story 356: Track section update
      track({
        name: 'section_updated',
        properties: {
          story_id: storyId,
          doc_type: docType,
          section_key: sectionKey,
          fields: Object.keys(updates),
        },
      });
    },
    onSettled: (_result, _error, { sectionKey }) => {
      // Invalidate document cache
      queryClient.invalidateQueries({ queryKey });
      // Invalidate history cache for this section (Story 232)
      // Get the section ID from current document data to invalidate specific history
      const currentData = queryClient.getQueryData<DocumentResponse>(queryKey);
      const section = currentData?.sections.find((s) => s.section_key === sectionKey);
      if (section?.id) {
        queryClient.invalidateQueries({
          queryKey: historyKeys.entity('section', section.id),
        });
      }
    },
  });

  const createSection = useMutation({
    mutationFn: async (section: SectionCreate): Promise<MutationResult> => {
      const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/documents/${docType}/sections`;
      return api.fetch(endpoint, {
        method: 'POST',
        body: JSON.stringify(section),
      });
    },
    onMutate: async (section) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<DocumentResponse>(queryKey);

      // Generate section key once - must be consistent between onMutate and onSuccess
      const generatedSectionKey = section.section_key ?? `section-${Date.now()}`;

      // Optimistically add the new section
      const newSection: DocumentSection = {
        section_key: generatedSectionKey,
        title: section.title,
        body: section.body,
        anchor: section.anchor ?? null,
        covers: null,
        seq: (previousData?.sections?.length ?? 0) + 1,
      };

      const updatedData: DocumentResponse = previousData
        ? {
            ...previousData,
            sections: [...previousData.sections, newSection],
          }
        : {
            doc_type: docType,
            summary: null,
            sections: [newSection],
          };

      queryClient.setQueryData<DocumentResponse>(queryKey, updatedData);

      return { previousData, generatedSectionKey };
    },
    onSuccess: (result, section, context) => {
      // Update the optimistically added section with the server-returned ID
      // This allows the history button to appear immediately (Story 232)
      if (result.id && context?.generatedSectionKey) {
        const currentData = queryClient.getQueryData<DocumentResponse>(queryKey);
        if (currentData) {
          queryClient.setQueryData<DocumentResponse>(queryKey, {
            ...currentData,
            sections: currentData.sections.map((s) =>
              s.section_key === context.generatedSectionKey ? { ...s, id: result.id } : s
            ),
          });
        }
      }
      // Story 356: Track section creation
      track({
        name: 'section_created',
        properties: {
          story_id: storyId,
          doc_type: docType,
          section_key: context?.generatedSectionKey ?? section.section_key ?? 'unknown',
        },
      });
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    // Note: No onSettled invalidation - onSuccess already updates with server-returned ID
    // Invalidating would cause a refetch that briefly shows data without the ID
  });

  const deleteSection = useMutation({
    mutationFn: async (sectionKey: string): Promise<MutationResult> => {
      const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/documents/${docType}/sections/${encodeURIComponent(sectionKey)}`;
      return api.fetch(endpoint, {
        method: 'DELETE',
      });
    },
    onMutate: async (sectionKey) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<DocumentResponse>(queryKey);

      if (previousData) {
        queryClient.setQueryData<DocumentResponse>(queryKey, {
          ...previousData,
          sections: previousData.sections.filter(
            (section) => section.section_key !== sectionKey
          ),
        });
      }

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSuccess: (_, sectionKey) => {
      // Story 356: Track section deletion
      track({
        name: 'section_deleted',
        properties: {
          story_id: storyId,
          doc_type: docType,
          section_key: sectionKey,
        },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      // Story 232: Invalidate deleted sections cache so "View Deleted" button appears
      queryClient.invalidateQueries({
        queryKey: historyKeys.deletedSections(projectSlug, storyId, docType),
      });
    },
  });

  const reorderSections = useMutation({
    mutationFn: async (order: string[]): Promise<MutationResult> => {
      const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/documents/${docType}/reorder`;
      return api.fetch(endpoint, {
        method: 'PATCH', // the worker admits GET, POST, PATCH and DELETE only; PUT is answered 405 before routing
        body: JSON.stringify({ order }),
      });
    },
    onMutate: async (order) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<DocumentResponse>(queryKey);

      if (previousData) {
        // Reorder sections based on new order array
        const sectionMap = new Map(
          previousData.sections.map((s) => [s.section_key, s])
        );
        const reorderedSections = order
          .map((key, index) => {
            const section = sectionMap.get(key);
            return section ? { ...section, seq: index + 1 } : null;
          })
          .filter((s): s is DocumentSection => s !== null);

        queryClient.setQueryData<DocumentResponse>(queryKey, {
          ...previousData,
          sections: reorderedSections,
        });
      }

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSuccess: (_, order) => {
      // Story 356: Track section reorder
      track({
        name: 'section_reordered',
        properties: {
          story_id: storyId,
          doc_type: docType,
          new_order: order,
        },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    updateSection,
    createSection,
    deleteSection,
    reorderSections,
  };
}
